import * as React from 'react';
import {AddIcon, CloseIcon, Dialog, Flex, Text} from "@fluentui/react-northstar";
import EditInput from "../Utils/EditInput";
import SharepointPicker from "../Utils/SharepointPicker";
import {uploadBlobFolder} from "../../apis/blobImageApi";
import {ImageGallery} from "../../Interfaces/ImageGallery";
import languageObject from "../../translations";
import {AppContext, IAppContextState} from "../../services/AppContext";

/**
 * FolderCreatorDialog props
 */
interface IFolderCreatorDialogProps {
    show: boolean,
    images: Array<ImageGallery>,
    onHide: () => void,
    onImagesUpdated: (images: Array<ImageGallery>) => Promise<void>
}

/**
 * FolderCreatorDialog state
 */
interface IFolderCreatorDialogState {
    folderName: string,
    folderLocation: string,
    updatingDatabase: boolean,
    forbiddenNames: Array<string>
}

/**
 * FolderCreatorDialog component
 */
class FolderCreatorDialog extends React.Component<IFolderCreatorDialogProps, IFolderCreatorDialogState> {
    // Define component context
    static contextType = AppContext;
    context!: IAppContextState;

    /**
     * Constructor
     */
    constructor(props: IFolderCreatorDialogProps) {
        super(props);

        this.state = {
            folderName: "",
            folderLocation: "",
            updatingDatabase: false,
            forbiddenNames: new Array<string>()
        }
    }

    /**
     * Component initialization
     */
    componentDidMount() {
    }

    /**
     * Allow component to render
     * @param nextProps next props
     */
    shouldComponentUpdate(nextProps: Readonly<IFolderCreatorDialogProps>): boolean {
        return this.props.show || nextProps.show;
    }

    /**
     * When component has updated
     * @param prevProps previous props
     * @param prevState previous state
     */
    componentDidUpdate(prevProps: Readonly<IFolderCreatorDialogProps>, prevState: Readonly<IFolderCreatorDialogState>) {
    }

    /**
     * Create a new folder
     */
    createFolder = () => {
        this.setState({updatingDatabase: true})
        const folderName = this.state.folderLocation + (this.state.folderLocation.endsWith("/") ? "" : "/") + this.state.folderName;
        uploadBlobFolder(folderName)
            .then(async success => {
                if (success) {
                    const image: ImageGallery = {imageLink: folderName, content: ""};
                    await this.props.onImagesUpdated([...this.props.images, image]);
                    this.props.onHide();
                    this.setState({
                        updatingDatabase: false,
                        folderName: "",
                        forbiddenNames: [...this.state.forbiddenNames, image.imageLink]
                    });
                } else {
                    console.error("Can't create folder");
                    this.setState({updatingDatabase: false, folderName: ""});
                }
            })
    }

    /**
     * Handle folder selection
     * @param path path
     * @param items items
     */
    handleSelect = (path: string, items: Array<string>) => {
        this.setState({folderLocation: path, forbiddenNames: items.filter(item => item.indexOf(".") === -1)})
    }

    /**
     * Check if folder name is not already existing
     */
    isFolderNameExisting = () => {
        return this.state.forbiddenNames.includes(this.state.folderLocation + this.state.folderName + "/");
    }

    /**
     * Render component
     */
    render() {
        const isAddButtonDisabled = !this.state.folderName
            || !this.state.folderLocation
            || this.state.updatingDatabase
            || this.isFolderNameExisting();

        return (
            <Dialog
                styles={{maxWidth: "600px"}}
                open={this.props.show}
                confirmButton={{
                    disabled: isAddButtonDisabled,
                    loading: this.state.updatingDatabase,
                    icon: <AddIcon outline/>,
                    content: languageObject.translate("Add"),
                    onClick: this.createFolder
                }}
                content={
                    <Flex column>
                        <EditInput
                            required
                            max={50}
                            errorMessage={this.isFolderNameExisting() ? languageObject.translate("FolderAlreadyExistingWithThisName") : ""}
                            disabled={this.state.updatingDatabase}
                            label={languageObject.translate("FolderName")}
                            value={this.state.folderName}
                            setValue={(value: string) => (value.length === 0 || value.match(/^[a-zA-Z0-9-_ ]+$/)) ?
                                this.setState({folderName: value}) : null}
                        />
                        <Text styles={{color: "gray", marginTop: "2px"}}
                              content={languageObject.translate("AvailableCharsFolderName")}/>
                        <Text className={"form-input"} styles={{marginBottom: "5px"}}
                              content={languageObject.translate("SelectParentFolder")}/>
                        <SharepointPicker
                            disabled={this.state.updatingDatabase}
                            onlyFolders
                            styles={{height: "300px"}}
                            items={this.props.images.map(img => img.imageLink)}
                            handleSelect={this.handleSelect}
                            noItemsContent={
                                <Flex fill column id={"sharepoint_picker"} hAlign={"center"} vAlign={"center"}>
                                    <Text content={languageObject.translate("NoFolder")}/>
                                </Flex>
                            }
                        />
                    </Flex>
                }
                header={languageObject.translate("NewFolder")}
                headerAction={{
                    icon: <CloseIcon outline/>,
                    title: 'Close',
                    onClick: this.props.onHide
                }}/>
        );
    }
}

export default FolderCreatorDialog;